import { Fragment, useEffect, useState } from "react";
import { Loader, Alert, Button, useMantineTheme, Group, Stepper, Progress, ActionIcon, Textarea, Card, Text, createStyles, Space } from "@mantine/core";
import { IconX, IconSearch } from "@tabler/icons-react";
import { IconAlertCircle } from "@tabler/icons-react";

const useStyles = createStyles((theme) => ({

    root: {
        position: 'relative',
    },

    input: {
        height: 'auto',
        paddingTop: 25,
        paddingBottom: 7,
        lineHeight: '1em'
    },

    label: {
        position: 'absolute',
        pointerEvents: 'none',
        fontSize: theme.fontSizes.xs,
        paddingLeft: theme.spacing.sm,
        paddingTop: 4,
        color: 'gray',
        zIndex: 1,
    },

    formWrapper: {
        width: '360px',
        overflowY: 'scroll',
        overflowX: 'hidden',
        height: 'calc(100vh - 120px)',
        direction: 'rtl',
        '&::-webkit-scrollbar': {
            backgroundColor: 'transparent',
            transform: 'translateY(-100px)',
            'width': '8px',
        },
        borderLeft: '2px solid #f1f1f1',
        '&:hover': {
            '&::-webkit-scrollbar-thumb': window.ontouchstart === undefined && {
                borderRadius: '8px',
                backgroundColor: 'rgba(0,0,0,0.5)',
            }
        },
        [theme.fn.smallerThan('sm')]: {
            margin: '0 auto',
            width: '100%',
            overflowY: 'initial',
            height: 'auto',
        },
    },

    innerForm: {
        position: 'relative',
        direction: 'ltr',
        width: '100%',
        padding: '0px 25px',
    },

    questionCard: {
        padding: '12px 15px !important',
        width: 'calc(100% + 30px)',
        left: '-15px',
        borderBottom: `1px solid ${theme.colors.gray[4]}`,
        cursor: 'pointer',
        // '&:hover': {
        //     background: theme.colors.gray[0],
        // },
    },

    cardDivider: {
        // display: 'block',
        position: 'relative',
        width: 'calc(100% + 30px)',
        left: '-15px',
    },

    searchWrapper: {
        position: 'sticky',
        top: '0px',
        zIndex: '99',
        background: 'white',
        backdropFilter: 'blur(5px)',
        width: 'calc(100% + 30px)',
        transform: 'translateX(-15px)',
        padding: '0px 15px',
    },

    searchCrossIcon: {
        marginRight: '7.5px'
    },

    stepper: {
        '.mantine-Stepper-stepBody': {
            width: '100%',
        },
    },

    retryButton: {
        width: '100%',
    },

    wrappedText: {
        wordBreak: 'break-all',
        whiteSpace: 'pre-wrap',
    },

}));

const getShownQuestions = (similarQuestions, searchString) => {
    // Return all indices of similarQuestions if searchString is empty
    if (searchString === '') return [...Array(similarQuestions.length).keys()];
    const lowerCaseSearchString = searchString.toLowerCase();
    const similarQuestionsWithTargetQuestion = [...similarQuestions];
    return similarQuestionsWithTargetQuestion.reduce((acc, { question, options }, index) => {
        const lowerCaseQuestion = question.toLowerCase();
        if (lowerCaseQuestion.includes(lowerCaseSearchString)) {
            acc.push(index);
        } else if (options.some(option => option.toLowerCase().includes(lowerCaseSearchString))) {
            acc.push(index);
        }
        return acc;
    }, []);
}


const stepText = [
    'Embedding target question',
    'Retrieving similar questions',
    'Fitting t-SNE',
];


const ConfidenceForm = ({
    embeddingsState,
    step,
    numTSNEStepsBeforePlot,
    similarQuestions,
    similarQuestionData,
    setSelectedEmbeddingIndex,
    surveyQuestion,
    shownQuestionIndices,
    setShownQuestionIndices,
    embeddingScores,
    embeddingsError,
    restartEmbeddings,
    similarDataPlotPrimary,
    selectedEmbeddingIndex,
    setSimilarDataPlotPrimary,
    model,
}) => {

    const [searchString, setSearchString] = useState('');
    useEffect(() => {
        // Scroll to top of form
        window.scrollTo(0, 0);
        const shownQuestionsForSearchString = getShownQuestions(similarQuestions, searchString);
        setShownQuestionIndices(shownQuestionsForSearchString);
        setSelectedEmbeddingIndex(-1);
    }, [searchString]);

    const { classes } = useStyles();

    const theme = useMantineTheme();

    const showStepper = embeddingsError || embeddingsState >= 0 && embeddingsState < stepText.length;
    const showBorder = showStepper === false && embeddingsError === false;

    const showAdditionalInfo = model.includes('GSS') === false;

    return (
        <div className={classes.formWrapper}>
            <div className={classes.innerForm}>
                <div className={classes.searchWrapper} style={{
                    borderBottom: showBorder ? `1px solid ${theme.colors.gray[4]}` : 'none',
                }}>
                    <Space h='md' />
                    <Text>Your question</Text>
                    <Space h={5} />
                    <Textarea
                        radius='md'
                        size='md'
                        autosize
                        disabled
                        value={surveyQuestion}
                        maxLength='750'
                    />
                    {
                        showStepper ?
                            <>
                                <Space h='xl' />
                                <Stepper
                                    active={embeddingsState}
                                    orientation="vertical"
                                    size='sm'
                                    className={classes.stepper}
                                >
                                    {
                                        stepText.map((text, index) => (
                                            <Stepper.Step
                                                key={index}
                                                label={`Step ${index + 1}`}
                                                style={{ width: '100%' }}
                                                loading={embeddingsState === index}
                                                completedIcon={embeddingsState === index && <Loader />}
                                                description={
                                                    index !== 2
                                                        ?
                                                        text
                                                        :
                                                        <Group position='apart'>
                                                            <div>
                                                                {text}
                                                            </div>
                                                            <Progress
                                                                radius='sm'
                                                                color="dark"
                                                                value={Math.round((step / numTSNEStepsBeforePlot) * 100)}
                                                                style={{
                                                                    flexGrow: 1,
                                                                    display: embeddingsState === 2 ? 'block' : 'none',
                                                                }}
                                                            />
                                                        </Group>
                                                }
                                            />
                                        ))
                                    }
                                </Stepper>
                            </>
                            :
                            embeddingsError === false &&
                            <>
                                <Space h='md' />
                                <Textarea
                                    className={classes.searchBar}
                                    placeholder='Search similar questions...'
                                    size='sm'
                                    autosize
                                    value={searchString}
                                    onChange={e => { setSearchString(e.target.value) }}
                                    maxLength='500'
                                    radius='lg'
                                    icon={<IconSearch size={20} stroke={1.5} />}
                                    rightSection={
                                        <ActionIcon
                                            hidden={searchString === ''}
                                            size={32}
                                            radius="xl"
                                            className={classes.searchCrossIcon}
                                            onMouseDown={() => { setSearchString('') }}
                                        >
                                            <IconX size={20} stroke={1.5} />
                                        </ActionIcon>
                                    }
                                />
                                <Space h={5} />
                                <Text size='xs' color='dimmed'>
                                    {
                                        model.includes('GSS') ?
                                            'Similar questions to the target question in the GSS training data are shown below.'
                                            :
                                            'Similar questions to the target question in your datasets are shown below.'
                                    }
                                </Text>
                                <Space h='md' />
                            </>
                    }
                </div>
                {
                    showStepper === false && embeddingsError === false &&
                    <>
                        {
                            shownQuestionIndices.length > 0 ?
                                similarQuestions.map(({ question, options }, index) => (
                                    shownQuestionIndices.includes(index) &&
                                    <Fragment key={index}>
                                        <Card
                                            key={index}
                                            className={classes.questionCard}
                                            // Hover events
                                            onMouseEnter={() => { setSelectedEmbeddingIndex(index) }}
                                            onMouseLeave={() => { if (!similarDataPlotPrimary) setSelectedEmbeddingIndex(null) }}
                                            onClick={() => { setSimilarDataPlotPrimary(!similarDataPlotPrimary) }}
                                            sx={{
                                                background: index === selectedEmbeddingIndex ? theme.colors.gray[0] : 'white',
                                            }}
                                        >
                                            {
                                                showAdditionalInfo && similarQuestionData[index][0]['product'] !== 'None' &&
                                                <Text weight={600}>Product: <Text component='span' weight={400}>{ similarQuestionData[index][0]['product']}</Text></Text>
                                            }
                                            <Text weight={600}>Question: <Text component='span' weight={400}>{question}</Text></Text>
                                            {/* {
                                                showAdditionalInfo &&
                                                <Text weight={600}>Options: <Text component='span' weight={400}>{options.join(', ')}</Text></Text>
                                            } */}
                                            {
                                                showAdditionalInfo &&
                                                <Text weight={600}>Country: <Text component='span' weight={400}>{ similarQuestionData[index][0]['country']}</Text></Text>
                                            }
                                            <Text weight={600}>Similarity score: <Text component='span' weight={400}>{embeddingScores[index]}</Text></Text>
                                            {
                                                showAdditionalInfo &&
                                                <>
                                                <Space h={8} />
                                                <Group position='apart' noWrap spacing = 'md'>
                                                <Text color='dimmed' size={11}>
                                                    {similarQuestionData[index][0]['question_id']}
                                                    </Text>
                                                    <Text color='dimmed' size={11} className={classes.wrappedText}>
                                                    {similarQuestionData[index][0]['dataset_id']}
                                                    </Text>
                                                    </Group>
                                                </>
                                            }
                                        </Card>
                                        {/* <Divider className={classes.cardDivider} /> */}
                                    </Fragment>
                                ))
                                :
                                <>
                                    <Space h='md' />
                                    <Text ta='center'>
                                        No questions found...
                                    </Text>
                                </>

                        }
                    </>
                }
                {
                    embeddingsError === true &&
                    <Alert icon={<IconAlertCircle size="1rem" />} color="red" radius='lg'>
                        <div>There was an error retrieving and plotting similar questions. Please try again.</div>
                        <Space h='xs' />
                        <Button
                            color='red'
                            onClick={restartEmbeddings}
                        >
                            Restart
                        </Button>
                    </Alert>
                }
            </div>
        </div >
    )
}

export default ConfidenceForm;